import { useMemo, useState } from "react";

import { api } from "../api/client";
import { AsyncBlock } from "../components/AsyncBlock";
import { BranchSearch } from "../components/BranchSearch";
import { Panel } from "../components/Panel";
import { PlanogramHeatmap } from "../components/PlanogramHeatmap";
import { Select } from "../components/Select";
import { heatColor } from "../lib/planogram";
import { useFilters } from "../lib/filters";
import { urlParam, useUrlState } from "../lib/urlState";
import { formatCount } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import type { CameraHeat } from "../types";
import { ui } from "../lib/ui";

/** Store heatmap: one branch's floor plan with each camera's view cone
 *  colored by occupancy, visits or dwell over the selected window. */

type Metric = "occupancy" | "visits" | "dwell";
type Band = "all" | "morning" | "afternoon" | "evening";

const METRICS: { key: Metric; label: string; hint: string }[] = [
  { key: "occupancy", label: "Occupancy", hint: "average people in view" },
  { key: "visits", label: "Visits", hint: "tracks passing through the view" },
  { key: "dwell", label: "Dwell", hint: "average time a visit stays in view" },
];

const BANDS: { key: Band; label: string; hours: { from: number; to: number } | null }[] = [
  { key: "all", label: "All day", hours: null },
  { key: "morning", label: "Morning 6–12", hours: { from: 6, to: 11 } },
  { key: "afternoon", label: "Afternoon 12–17", hours: { from: 12, to: 16 } },
  { key: "evening", label: "Evening 17–23", hours: { from: 17, to: 23 } },
];

const isBand = (v: string | null): v is Band => BANDS.some((b) => b.key === v);
const isMetric = (v: string | null): v is Metric =>
  METRICS.some((m) => m.key === v);

function metricValue(cam: CameraHeat, metric: Metric): number {
  switch (metric) {
    case "occupancy":
      return cam.avg_occupancy;
    case "visits":
      return cam.tracks;
    case "dwell":
      return cam.avg_dwell_seconds;
  }
}

function formatMetric(value: number, metric: Metric): string {
  switch (metric) {
    case "occupancy":
      return value.toFixed(2);
    case "visits":
      return formatCount(value);
    case "dwell":
      return value >= 90 ? `${(value / 60).toFixed(1)} min` : `${Math.round(value)} s`;
  }
}

export function Heatmap() {
  const { branches, governorates, range, setRange, defaultBranch, single } = useFilters();
  // A branch PC's heatmap is its own store's; the hub picks one.
  const [governorate, setGovernorate] = useState(() =>
    defaultBranch ? defaultBranch.governorate : urlParam("gov") ?? "",
  );
  const [branchId, setBranchId] = useState(() =>
    defaultBranch ? defaultBranch.id : urlParam("branch") ?? "",
  );
  const [band, setBand] = useState<Band>(() => {
    const v = urlParam("band");
    return isBand(v) ? v : "all";
  });
  const [metric, setMetric] = useState<Metric>(() => {
    const v = urlParam("metric");
    return isMetric(v) ? v : "occupancy";
  });
  const [showCameras, setShowCameras] = useState(() => urlParam("cams") === "1");
  // Transient: picking a result puts the branch itself in the URL.
  const [search, setSearch] = useState("");

  useUrlState(
    {
      gov: single ? "" : governorate,
      branch: single ? "" : branchId,
      band: band === "all" ? "" : band,
      metric: metric === "occupancy" ? "" : metric,
      cams: showCameras ? "1" : "",
      from: range.from,
      to: range.to,
    },
    (p) => {
      if (!single) {
        if ("gov" in p) setGovernorate(p.gov);
        if ("branch" in p) setBranchId(p.branch);
        else if ("gov" in p) setBranchId("");
      }
      if ("band" in p) setBand(isBand(p.band) ? p.band : "all");
      if ("metric" in p) setMetric(isMetric(p.metric) ? p.metric : "occupancy");
      if ("cams" in p) setShowCameras(p.cams === "1");
      if ("from" in p || "to" in p) {
        setRange({ from: p.from || range.from, to: p.to || range.to });
      }
    },
  );

  // The search spans every store, not just the governorate drilled into —
  // the same box and the same matching as the Dashboard and Fleet.
  const searchable = useMemo(
    () =>
      branches.map((b) => ({
        id: b.id,
        name: b.name,
        governorate: b.governorate,
        area: b.area,
      })),
    [branches],
  );

  const branchOptions = useMemo(() => {
    const list = branches.filter(
      (b) => !governorate || b.governorate === governorate,
    );
    const byArea = new Map<string, typeof list>();
    for (const b of list) {
      const group = byArea.get(b.area) ?? [];
      group.push(b);
      byArea.set(b.area, group);
    }
    return [...byArea.entries()];
  }, [branches, governorate]);

  const hours = BANDS.find((b) => b.key === band)?.hours ?? null;
  const heat = useAsync(
    () => (branchId ? api.heatmap(branchId, range, hours) : Promise.resolve(null)),
    [branchId, range.from, range.to, band],
  );

  const data = heat.data;
  const plan = data?.planogram ?? null;
  const markerIds = new Set(plan?.cameras.map((c) => c.id) ?? []);
  const values = Object.fromEntries(
    (data?.cameras ?? []).map((c) => [c.camera_id, metricValue(c, metric)]),
  );
  const max = Math.max(0, ...Object.values(values));
  const orphans = (data?.cameras ?? []).filter((c) => !markerIds.has(c.camera_id));
  const branch = branches.find((b) => b.id === branchId);

  return (
    <div className="flex flex-col gap-5">
      <Panel
        title="Store heatmap"
        subtitle="Per-camera occupancy projected onto the branch floor plan grid — only floor a camera actually sees (views are cut by walls and tall fixtures) gets heat"
      >
        <div className={`${ui.toolbar} mb-4`}>
          {!single && (
          <div className="flex flex-wrap items-center gap-2">
            <span className={ui.label}>Branch</span>
            <Select
              value={governorate}
              onChange={(e) => {
                setGovernorate(e.target.value);
                setBranchId("");
              }}
            >
              <option value="">Governorate…</option>
              {governorates.map((g) => (
                <option key={g.governorate} value={g.governorate}>
                  {g.governorate}
                </option>
              ))}
            </Select>
            <Select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              disabled={!governorate && branches.length > 40}
            >
              <option value="">
                {governorate || branches.length <= 40
                  ? "Branch…"
                  : "Pick a governorate first"}
              </option>
              {branchOptions.map(([area, list]) => (
                <optgroup key={area || "—"} label={area || "—"}>
                  {list.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
            <BranchSearch
              className="min-w-[240px]"
              branches={searchable}
              value={search}
              onChange={setSearch}
              onSelect={(b) => {
                setSearch(b.name);
                setGovernorate(b.governorate);
                setBranchId(b.id);
              }}
            />
          </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className={ui.label}>Dates</span>
            <input
              type="date"
              className={ui.input}
              value={range.from}
              max={range.to}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
            />
            <span className="text-muted">→</span>
            <input
              type="date"
              className={ui.input}
              value={range.to}
              min={range.from}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={ui.label}>Hours</span>
            {BANDS.map((b) => (
              <button
                key={b.key}
                type="button"
                className={`${ui.chip} ${band === b.key ? ui.chipOn : ""}`}
                onClick={() => setBand(b.key)}
              >
                {b.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={ui.label}>Color by</span>
            {METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`${ui.chip} ${metric === m.key ? ui.chipOn : ""}`}
                title={m.hint}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={ui.label}>Cameras</span>
            <button
              type="button"
              className={`${ui.chip} ${showCameras ? ui.chipOn : ""}`}
              aria-pressed={showCameras}
              title="Overlay each camera's view cone, marker and label on the heat grid"
              onClick={() => setShowCameras(!showCameras)}
            >
              {showCameras ? "Cameras shown" : "Cameras hidden"}
            </button>
          </div>
        </div>

        {!branchId ? (
          <p className="text-muted">
            Pick a branch to see its heatmap. Occupancy is recorded per camera
            by pipeline runs: every camera's footage goes in a folder named by
            its plan marker id
            (<span className="font-mono text-xs">&lt;day&gt;/cam2/…</span>) —
            entrance cameras count entries too, the rest are occupancy-only.
          </p>
        ) : (
          <AsyncBlock loading={heat.loading} error={heat.error}>
            {!plan ? (
              <p className="text-muted">
                {branch?.name ?? branchId} has no floor plan yet — draw one
                {single ? " on the Store page" : " on its Store page"} (Floor plan &amp;
                cameras) to place its cameras.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                <PlanogramHeatmap
                  plan={plan}
                  values={values}
                  max={max}
                  format={(v) => formatMetric(v, metric)}
                  showCameras={showCameras}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <span className={ui.label}>
                    {METRICS.find((m) => m.key === metric)?.hint}
                  </span>
                  <div
                    className="h-2.5 w-44 rounded-full"
                    style={{
                      background: `linear-gradient(to right, ${heatColor(0)}, ${heatColor(0.5)}, ${heatColor(1)})`,
                    }}
                  />
                  <span className="text-xs text-muted">
                    0 → {max > 0 ? formatMetric(max, metric) : "—"}
                  </span>
                  {(data?.cameras.length ?? 0) === 0 && (
                    <span className="text-xs text-muted">
                      No occupancy data in this window yet — it appears after a
                      pipeline run processes footage for this branch.
                    </span>
                  )}
                </div>
                {orphans.length > 0 && (
                  <p className={`${ui.notice} ${ui.noticeError} mb-0`}>
                    Footage folders without a matching camera on the floor plan:{" "}
                    {orphans.map((c) => c.camera_id).join(", ")}. Rename the
                    folders to a marker id, or add the missing cameras to the
                    plan.
                  </p>
                )}
                {(data?.cameras.length ?? 0) > 0 && (
                  <table className={ui.table}>
                    <thead>
                      <tr>
                        <th>Camera</th>
                        <th>Avg occupancy</th>
                        <th>Visits</th>
                        <th>Avg dwell</th>
                        <th>Hours with data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.cameras ?? []).map((cam) => {
                        const marker = plan.cameras.find((c) => c.id === cam.camera_id);
                        return (
                          <tr key={cam.camera_id}>
                            <td>
                              <strong>{marker?.label || cam.camera_id}</strong>
                              {marker?.entry && (
                                <span className={`${ui.pill} ml-2`}>entry</span>
                              )}
                              <div className="font-mono text-xs text-muted">
                                {cam.camera_id}
                              </div>
                            </td>
                            <td>{formatMetric(cam.avg_occupancy, "occupancy")}</td>
                            <td>{formatMetric(cam.tracks, "visits")}</td>
                            <td>{formatMetric(cam.avg_dwell_seconds, "dwell")}</td>
                            <td>{cam.hours_covered}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </AsyncBlock>
        )}
        <p className="mt-3 text-xs text-muted">
          Heat is an hourly aggregate per camera (people in view, visits,
          dwell). This view never reads per-person records; branch-scoped
          tracks and their face enrichment are available separately in the
          Tracks viewer.
        </p>
      </Panel>
    </div>
  );
}
